/**
 * popupBannerStore.ts
 * Manages site-wide popups and announcement banners.
 * Stored in localStorage — no size concerns (text only).
 */

export type BannerStyle = "info" | "success" | "warning" | "promo";
export type PopupTrigger = "on-load" | "on-exit" | "after-delay";
export type BannerPosition = "top" | "bottom";
export type BannerThickness = "slim" | "normal" | "tall";

/** Special ID for the email-capture notify banner — driven by NotifyBanner component */
export const NOTIFY_BANNER_ID = "banner-notify-default";

export interface SiteBanner {
  id: string;
  type: "banner";
  enabled: boolean;
  text: string;
  subText?: string;
  style: BannerStyle;
  dismissible: boolean;
  ctaLabel?: string;
  ctaUrl?: string;
  position?: BannerPosition;   // "top" (default) or "bottom"
  thickness?: BannerThickness; // "slim" | "normal" (default) | "tall"
  createdAt: string;
  updatedAt: string;
}

export interface SitePopup {
  id: string;
  type: "popup";
  enabled: boolean;
  title: string;
  body: string;
  style: BannerStyle;
  trigger: PopupTrigger;
  delaySeconds: number;
  ctaLabel?: string;
  ctaUrl?: string;
  showOnce: boolean;
  createdAt: string;
  updatedAt: string;
}

export type SiteNotification = SiteBanner | SitePopup;

const STORAGE_KEY = "ma_site_notifications";

/** Stage notifications for Supabase sync on next Save */
function stageNotifications(items: SiteNotification[]): void {
  try {
    import("@/hooks/useAdminStorage").then(({ stagingStoreSet, stagingStoreNotify }) => {
      stagingStoreSet(STORAGE_KEY, items);
      stagingStoreNotify(STORAGE_KEY);
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

export function loadNotifications(): SiteNotification[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as SiteNotification[];
  } catch {
    return [];
  }
}

export function saveNotification(item: SiteNotification): void {
  const all = loadNotifications();
  const idx = all.findIndex((n) => n.id === item.id);
  if (idx >= 0) {
    all[idx] = item;
  } else {
    all.push(item);
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  stageNotifications(all);
}

export function deleteNotification(id: string): void {
  const all = loadNotifications().filter((n) => n.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  stageNotifications(all);
}

export function toggleNotification(id: string): void {
  const all = loadNotifications();
  const idx = all.findIndex((n) => n.id === id);
  if (idx >= 0) {
    all[idx] = { ...all[idx], enabled: !all[idx].enabled, updatedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    stageNotifications(all);
  }
}

export function getActiveBanners(): SiteBanner[] {
  return loadNotifications().filter((n): n is SiteBanner => n.type === "banner" && n.enabled);
}

export function getActivePopups(): SitePopup[] {
  return loadNotifications().filter((n): n is SitePopup => n.type === "popup" && n.enabled);
}

// ─── One-time migration: seed the "Be the first to know" banner ─────────────
// This runs once on first load. If no notifications exist yet, we create the
// default "Be the first to know" banner so it shows up in Popups & Banners tab.
const SEEDED_KEY = "ma_notifications_seeded_v1";

export function seedDefaultBannerIfNeeded(): void {
  try {
    if (localStorage.getItem(SEEDED_KEY)) return; // already seeded
    const existing = loadNotifications();
    if (existing.length > 0) {
      // Already has notifications — just mark as seeded
      localStorage.setItem(SEEDED_KEY, "1");
      return;
    }
    // Create the default "Be the first to know" banner
    const defaultBanner: SiteBanner = {
      id: "banner-notify-default",
      type: "banner",
      enabled: true,
      text: "Be the first to know when we open our doors!",
      subText: "Sign up for updates below.",
      style: "info",
      dismissible: true,
      ctaLabel: "Get Notified",
      ctaUrl: "#application",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify([defaultBanner]));
    stageNotifications([defaultBanner]);
    localStorage.setItem(SEEDED_KEY, "1");
  } catch { /* ignore */ }
}

const DISMISSED_KEY = "ma_dismissed_notifications";

export function isDismissed(id: string): boolean {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    const set: string[] = JSON.parse(raw);
    return set.includes(id);
  } catch {
    return false;
  }
}

export function dismissNotification(id: string): void {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    const set: string[] = raw ? JSON.parse(raw) : [];
    if (!set.includes(id)) {
      set.push(id);
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(set));
    }
  } catch { /* ignore */ }
}

// ─── Staging-only operations (no localStorage write until Save) ───────────────

/** Stage notifications to the global staging store without writing to localStorage.
 *  This is used in admin mode where changes only commit when the user clicks Save.
 */
export function stageNotificationsToStaging(items: SiteNotification[]): void {
  try {
    import("@/hooks/useAdminStorage").then(({ stagingStoreSet, stagingStoreNotify }) => {
      stagingStoreSet(STORAGE_KEY, items);
      stagingStoreNotify(STORAGE_KEY);
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

/** Commit staged notifications to localStorage */
export function commitNotifications(): void {
  // localStorage is already up-to-date since we use it as the "working copy" in admin mode
  // The staging layer ensures supabase sync picks it up on Save
  try {
    const items = loadNotifications();
    import("@/hooks/useAdminStorage").then(({ stagingStoreSet, stagingStoreNotify }) => {
      stagingStoreSet(STORAGE_KEY, items);
      stagingStoreNotify(STORAGE_KEY);
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}
